import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import NotificationContent from './NotificationContent';
import * as styles from './NotificationPopOver.module.scss';
import PopOver from '../components/deprecated/PopOver';
import IconButton from '../components/IconButton';
import ModeToggle from '../components/ModeToggle';
import StickyHeader from '../components/StickyHeader';
import SVGIcon from '../components/SVGIcon';
import Tooltip from '../components/Tooltip';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import markAsReadIcon from '../icons/non-prefilled/16x16/markAsRead.svg';

const CLEAR_ALL_NOTIFICATIONS_MUTATION = gql(/** @complexity 4 */ `
  mutation clearAllNotifications {
    markNotifications(status: READ) {
      id
      status
    }
  }
`);

// eslint-disable-next-line slab-graphql/no-unused-fields
const UNREAD_NOTIFICATIONS_QUERY = gql(/** @complexity 4 */ `
  query NotificationPopOver {
    session: currentSession {
      organization {
        id
        unreadNotifications {
          edges {
            __typename
          }
        }
      }
    }
  }
`);

const NOTIFICATIONS_FRAGMENT = gql(`
  fragment NotificationPopOver_notifications on Notification {
    id
    # eslint-disable-next-line @graphql-eslint/require-selections
    ...NotificationContent_notifications
  }
`);

type NotificationsFragment = DocumentType<typeof NOTIFICATIONS_FRAGMENT>;

interface NotificationPopOverProps {
  unreadNotifications?: NotificationsFragment[];
  allNotifications?: NotificationsFragment[];
  fetchMoreUnreadNotifications?: (() => void) | null;
  fetchMoreAllNotifications?: (() => void) | null;
  onNavigate?: () => void;
}

const NotificationPopOver = ({
  unreadNotifications = [],
  allNotifications = [],
  fetchMoreUnreadNotifications = null,
  fetchMoreAllNotifications = null,
  onNavigate,
}: NotificationPopOverProps) => {
  const [mode, setMode] = useState<'unread' | 'all'>('unread');

  const [fetchMore, notifications] =
    mode === 'all'
      ? [fetchMoreAllNotifications, allNotifications]
      : [fetchMoreUnreadNotifications, unreadNotifications];

  const [clear] = useMutation(CLEAR_ALL_NOTIFICATIONS_MUTATION, {
    update: (store) => {
      const data = store.readQuery({ query: UNREAD_NOTIFICATIONS_QUERY });
      if (!data) return;
      store.writeQuery({
        query: UNREAD_NOTIFICATIONS_QUERY,
        data: {
          ...data,
          session: {
            ...data.session,
            organization: {
              ...data.session.organization,
              unreadNotifications: {
                ...data.session.organization.unreadNotifications,
                edges: [],
              },
            },
          },
        },
      });
    },
  });

  return (
    <PopOver.Container noPadding width="wide">
      <StickyHeader>
        <div className={styles.header}>
          <h4 className={styles.title}>Notifications</h4>
          <ModeToggle
            left="Unread"
            right="All"
            leftActive={mode === 'unread'}
            onToggle={() => setMode(mode === 'unread' ? 'all' : 'unread')}
          />
          <div className={styles.markAll}>
            <Tooltip content="Mark all as read">
              <IconButton onClick={() => clear()} aria-label="Mark all as read">
                <SVGIcon size="large">{markAsReadIcon}</SVGIcon>
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </StickyHeader>
      <NotificationContent
        // make sure infinite scroll is re-mounted after switching between tabs
        key={mode}
        notifications={notifications}
        fetchMore={fetchMore}
        onNavigate={onNavigate}
      />
    </PopOver.Container>
  );
};

export default NotificationPopOver;
